import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import WEBAPI from '@/services/WEBAPI';
import { setDeeplinkData } from '../actions';
import {
  findApplicationTypeByCode,
  isApplicationsAction,
  normalizeLanguageForPublicTypes,
} from '../routeUtils';

const buildApplicationsMeta = ({
  applicationsCode,
  applicationsLicensePlate,
  publicApplicationType = null,
  publicApplicationTypes = [],
  publicApplicationTypesError = null,
}) => ({
  application_licence_plate: applicationsLicensePlate || null,
  applications_code: applicationsCode || null,
  public_application_types: publicApplicationTypes,
  public_application_types_error: publicApplicationTypesError,
  public_application_type: publicApplicationType,
  public_application_type_found: !!publicApplicationType,
});

const usePublicApplicationTypes = ({ appLanguage, queryParams }) => {
  const dispatch = useDispatch();
  const [isFetchingApplicationTypes, setIsFetchingApplicationTypes] =
    useState(false);
  const [hasFetchedApplicationTypes, setHasFetchedApplicationTypes] =
    useState(false);

  useEffect(() => {
    if (!isApplicationsAction(queryParams.action) || !queryParams.authority) {
      setIsFetchingApplicationTypes(false);
      setHasFetchedApplicationTypes(true);
      return;
    }

    let isCancelled = false;

    const fetchPublicApplicationTypes = async () => {
      setIsFetchingApplicationTypes(true);
      setHasFetchedApplicationTypes(false);

      try {
        const response = await WEBAPI.getPublicApplicationTypes(
          queryParams.authority,
          normalizeLanguageForPublicTypes(appLanguage)
        );

        if (isCancelled) return;

        const publicApplicationTypes = response.result || [];
        const publicApplicationType = findApplicationTypeByCode(
          publicApplicationTypes,
          queryParams.code
        );

        dispatch(
          setDeeplinkData({
            action: queryParams.action || null,
            payload_code: queryParams.code || null,
            authority: queryParams.authority || null,
            country: queryParams.country || null,
            meta: buildApplicationsMeta({
              applicationsCode: queryParams.applicationsCode,
              applicationsLicensePlate: queryParams.applicationsLicensePlate,
              publicApplicationType: publicApplicationType || null,
              publicApplicationTypes,
            }),
            needsDeeplinkRedirection: true,
          })
        );
      } catch (error) {
        if (isCancelled) return;

        dispatch(
          setDeeplinkData({
            action: queryParams.action || null,
            payload_code: queryParams.code || null,
            authority: queryParams.authority || null,
            country: queryParams.country || null,
            meta: buildApplicationsMeta({
              applicationsCode: queryParams.applicationsCode,
              applicationsLicensePlate: queryParams.applicationsLicensePlate,
              publicApplicationTypesError: true,
            }),
            needsDeeplinkRedirection: true,
          })
        );
      } finally {
        if (!isCancelled) {
          setIsFetchingApplicationTypes(false);
          setHasFetchedApplicationTypes(true);
        }
      }
    };

    fetchPublicApplicationTypes();

    return () => {
      isCancelled = true;
    };
  }, [
    appLanguage,
    dispatch,
    queryParams.action,
    queryParams.applicationsCode,
    queryParams.applicationsLicensePlate,
    queryParams.authority,
    queryParams.code,
    queryParams.country,
  ]);

  return (
    isApplicationsAction(queryParams.action) &&
    queryParams.authority &&
    (!hasFetchedApplicationTypes || isFetchingApplicationTypes)
  );
};

export default usePublicApplicationTypes;
